<script setup lang="ts">
import { nextTick, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { MonthlyPurchase, Trainer } from "../../types/PersologOnlineTypes.ts";
import { ProfileAdministrationAPI } from "../../api";
import SuccessIcon from "./SuccessIcon.vue";
import { bringFocusToElementById } from "../../utils/GeneralUtils.ts";

const { t } = useI18n();
const visible = ref(false);
const quantity = ref(0);
const purchasedQuantity = ref(0);
const loading = ref(false);
const showPurchasedFailedDialog = ref(false);

const props = defineProps({
    product: {
        type: String,
        required: true,
    },
    productName: {
        type: String,
        required: true,
    },
    shopLink: {
        type: String,
        default:
            "https://shop.persolog.com/auth/redirect/login?redirectTo=frontend.account.home.page&utm_source=persolog+online&utm_medium=anzeige",
    },
    trainer: {
        type: Object as () => Trainer,
        required: true,
    },
    withLabel: {
        type: Boolean,
        default: true,
    },
    withClasses: {
        type: String,
        default: "",
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    postfix: {
        type: String,
        default: "",
    }
});

const emit = defineEmits(["update"]);

const purchaseSuccess = ref(false);
const availableLimit = ref(undefined);
const agreeToCosts = ref(false);

function buyProduct() {
    if (quantity.value > 0 && agreeToCosts.value) {
        purchasedQuantity.value = quantity.value;
        loading.value = true;

        // No date here: the backend stamps purchases with the server clock so a
        // wrong device clock can't end up in the billing records.
        const purchase: Omit<MonthlyPurchase, "date"> = {
            trainer: props.trainer?.id as string,
            product: props.product as string,
            quantity: quantity.value,
        };

        ProfileAdministrationAPI.buyProduct(purchase)
            .then(() => {
                purchaseSuccess.value = true;
            })
            .catch((error) => {
                console.error("Purchase failed:", error);
                showPurchasedFailedDialog.value = true;
            })
            .finally(() => {
                loading.value = false;
                visible.value = false;
            });
    } else {
        bringFocusToElementById(`buy-button_input_${props.product}`);
        return;
    }
    agreeToCosts.value = false;
    quantity.value = 0;
}

async function toggle() {
    visible.value = !visible.value;
    availableLimit.value = availableLimit.value = await ProfileAdministrationAPI.getAvailableMonthlyLimit(
        props.trainer.id,
    );
}

function openShop() {
    globalThis.open(props.shopLink, "_blank");
}

function closeDialog() {
    visible.value = false;
    quantity.value = 0;
    agreeToCosts.value = false;
}

function bringFocusToDialog() {
    if (visible.value) {
        nextTick(() => {
            try {
                bringFocusToElementById(`buy-button_input_${props.product}`);
            } catch (error) {
                console.warn('Could not focus element:', error);
            }
        });
    }
}

watch(purchaseSuccess, (newVal) => {
    if (!newVal) {
        purchaseSuccess.value = false;
        loading.value = false;
        visible.value = false;
        emit("update", true);
    }
});
</script>

<template>
    <SuccessIcon
        :text-icon="'+' + purchasedQuantity"
        :visible="purchaseSuccess"
        :length="500"
        :pop-confetti="withLabel"
        @update:visible="purchaseSuccess = false"
    />
    <Button
        v-if="trainer.distributor.id === 1 && trainer.purchaseLimit === 0"
        :id="'buy-button_btn_' + product + props.postfix"
        :outlined="withLabel"
        :class="withClasses"
        :disabled="disabled"
        @click="openShop"
        icon="pi pi-shopping-cart"
        :label="withLabel ? t('buy-button.buyProfiles') : ''"
        v-tooltip.top="{
            value: t('buy-button.prepaid'),
            showDelay: 500,
        }"
    />
    <Button
        v-if="trainer.purchaseLimit > 0"
        :id="'buy-button_btn_' + product + props.postfix"
        :class="withClasses"
        :outlined="withLabel"
        :disabled="disabled"
        @click="toggle"
        icon="pi pi-shopping-cart"
        :label="withLabel ? t('buy-button.buyProfiles') : ''"
        v-tooltip.top="{
            value: t('buy-button.billing'),
            showDelay: 500,
        }"
    />
    <Dialog
        :id="'buy-button_dialog_' + product"
        v-model:visible="visible"
        modal
        :style="{ maxWidth: '90vw' }"
        :header="t('buy-button.modalHeader', { profileName: productName })"
        position="center"
        :dismissable-mask="false"
        :block-scroll="true"
        :draggable="false"
        :closable="false"
        class="buy-dialog"
        @show="bringFocusToDialog()"
    >
        <div class="dialog-content mt-2" :id="'buy-button_div_' + product + '_input-number'">
            <InputNumber
                v-model="quantity"
                :input-id="'buy-button_input_' + product"
                show-buttons
                button-layout="horizontal"
                decrement-button-class="p-button-secondary"
                increment-button-class="p-button-secondary"
                increment-button-icon="pi pi-plus"
                decrement-button-icon="pi pi-minus"
                mode="decimal"
                class="in"
                :min="0"
                :max="availableLimit"
                :suffix="
                    ' ' +
                    t('buy-button.profiles', {
                    count: quantity,
                    })"
            />
            <div class="limit-info-section">
                <span v-if="availableLimit === undefined">
                    <i class="pi pi-spinner pi-spin"></i>
                </span>
                <small v-else>
                    <b>{{ t("buy-button.availableLimit") }}: {{ availableLimit }}</b>
                </small>
                <i v-if="availableLimit === 0" class="pi pi-exclamation-triangle ml-2"></i>
            </div>
        </div>

        <!-- Cost agreement checkbox -->
        <div class="cost-agreement-container mt-4" v-if="availableLimit !== undefined">
            <Checkbox
                :input-id="'buy-button_checkbox_' + product + '_costs'"
                v-model="agreeToCosts"
                :binary="true"
            />
            <label
                :for="'buy-button_checkbox_' + product + '_costs'"
                class="cost-agreement-label ml-2"
            >
                {{ t("buy-button.agreeToCosts") }}
            </label>
        </div>
        <template #footer>
            <Button
                :id="'buy-button_btn_' + product + '_cancel'"
                severity="secondary"
                :label="t('buttons.cancel')"
                icon="pi pi-times"
                @click="closeDialog"
                text
            />
            <Button
                :id="'buy-button_btn_' + product + '_buy'"
                severity="secondary"
                @click="buyProduct"
                :label="t('buttons.buy')"
                :icon="loading ? 'pi pi-spin pi-spinner' : 'pi pi-shopping-bag'"
                :disabled="!agreeToCosts || loading"
                autofocus
            />
        </template>
    </Dialog>

    <Dialog
        :id="'buy-button_dialog_' + product + '_failed'"
        v-model:visible="showPurchasedFailedDialog"
        modal
        :header="t('buy-button.modalHeaderFail', { profileName: productName })"
        position="center"
        :dismissable-mask="false"
        :block-scroll="true"
        :draggable="false"
    >
        <span class="justify-center">{{ t("buy-button.modalTextFail") }}</span>
        <template #footer>
            <Button
                :id="'buy-button_btn_' + product + '_failed_close'"
                severity="secondary"
                :label="t('buttons.close')"
                icon="pi pi-times"
                @click="showPurchasedFailedDialog = false"
                text
            />
        </template>
    </Dialog>
</template>

<style scoped>

.dialog-content {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.input-section {
    width: 100%;
}

.input-section :deep(.p-inputnumber-input) {
    width: 100%;
    min-width: 100px;
}

.input-section :deep(.p-inputtext) {
    text-align: center;
}

.limit-info-section {
    justify-content: flex-start;
}

.cost-agreement-container {
    display: flex;
    align-items: flex-start;
    margin-top: 1rem;
    padding: 0.75rem;
    background-color: var(--p-surface-50);
    border-radius: 6px;
    border-left: 3px solid var(--p-primary-500);
    border-right: 3px solid var(--p-primary-500);
}

.cost-agreement-label {
    font-size: 0.875rem;
    line-height: 1.4;
    color: var(--p-text-color);
    cursor: pointer;
    user-select: none;
}

.cost-agreement-label:hover {
    color: var(--p-primary-500);
}

@media screen and (max-width: 600px) {
    :global(.buy-dialog) {
        width: 95vw !important;
        max-width: 95vw !important;
    }

    .in {
        width: 100%;
    }

    .in :deep(.p-inputnumber-input) {
        width: 0;
        flex: 1 1 0;
        min-width: 0;
        font-size: 0.85rem;
    }
}
</style>
